/**
 * benvel-size-guide-modal.js — Story 20.1
 * Singleton size-guide modal: deep-link recognizer, open/close,
 * focus management, unit toggle, consent-gated analytics event.
 *
 * Budget target: < 5 KB gzipped.
 * Loaded as: <script src="…benvel-size-guide-modal.js" defer>
 *
 * Consumer trigger pattern (documented here; consumers opt in additively):
 *   <a
 *     href="/pages/guia-de-tallas?section=body-measurements"
 *     data-bv-size-guide-trigger="body-measurements"
 *     class="size-guide-modal-trigger"
 *   >{{ 'size_guide.modal.trigger_cta' | t }}</a>
 *
 *   Trigger attribute values: body-measurements | measurement-guide |
 *   garment-table | unit-toggle | fit-recommendation | (empty = open at top)
 *
 * PII discipline: analytics event detail contains only opaque IDs
 *   (section, sourcePage enum, durationMs). No email, no phone, no customer.id.
 */

(function () {
  'use strict';

  /** Valid in-modal section anchor values */
  const VALID_SECTIONS = [
    'body-measurements',
    'measurement-guide',
    'garment-table',
    'unit-toggle',
    'fit-recommendation',
  ];

  /** @type {HTMLElement | null} */
  let host = null;
  /** @type {HTMLDialogElement | null} */
  let dialog = null;
  /** @type {Element | null} */
  let openedFromTrigger = null;
  /** @type {number} openedAt timestamp */
  let openedAt = 0;

  // ─── Initialise on DOMContentLoaded ─────────────────────────────────────

  document.addEventListener('DOMContentLoaded', function () {
    host = document.querySelector('benvel-size-guide-dialog');
    if (!host) return;

    dialog = /** @type {HTMLDialogElement} */ (host.querySelector('dialog.size-guide-modal'));
    if (!dialog) return;

    // Wire scrim close: click on the <dialog> backdrop element itself
    dialog.addEventListener('click', function (e) {
      if (e.target === dialog) {
        closeDialog();
      }
    });

    // Wire close button (declarative on:click="/closeDialog" handled by Horizon web
    // component runtime; also add a direct listener for resilience)
    const closeBtn = dialog.querySelector('.size-guide-modal__close');
    if (closeBtn) {
      closeBtn.addEventListener('click', function () {
        closeDialog();
      });
    }

    // Delegate trigger clicks
    document.addEventListener('click', function (e) {
      const trigger = /** @type {Element} */ (e.target).closest('[data-bv-size-guide-trigger]');
      if (!trigger) return;
      e.preventDefault();
      openedFromTrigger = trigger;
      const section = trigger.getAttribute('data-bv-size-guide-trigger') || '';
      openSizeGuide(section || undefined);
    });

    // Unit toggle
    const toggleContainer = dialog.querySelector('[data-bv-size-guide-section="unit-toggle"]');
    if (toggleContainer) {
      toggleContainer.addEventListener('click', function (e) {
        const btn = /** @type {Element} */ (e.target).closest('.size-guide-modal__unit-btn');
        if (!btn) return;
        const unit = btn.getAttribute('data-unit');
        if (unit) setUnit(unit);
      });
    }

    // Analytics close event
    dialog.addEventListener('close', function () {
      emitCloseEvent();
      // Return focus to the element that opened the modal
      if (openedFromTrigger && typeof openedFromTrigger.focus === 'function') {
        /** @type {HTMLElement} */ (openedFromTrigger).focus();
      }
      openedFromTrigger = null;
    });

    // Deep-link: parse ?section= on page load
    const sectionParam = new URLSearchParams(location.search).get('section');
    if (sectionParam && VALID_SECTIONS.includes(sectionParam)) {
      openSizeGuide(sectionParam);
    }
  });

  // ─── Public API ──────────────────────────────────────────────────────────

  /**
   * Open the size-guide modal, optionally scrolled to a named section.
   * @param {string} [section] - one of VALID_SECTIONS, or undefined for top
   */
  function openSizeGuide(section) {
    if (!dialog) return;

    openedAt = Date.now();
    dialog.showModal();

    if (section && VALID_SECTIONS.includes(section)) {
      // Scroll the matching section into view
      const target = dialog.querySelector('[data-bv-size-guide-section="' + section + '"]');
      if (target) {
        // Use requestAnimationFrame to let the dialog fully render first
        requestAnimationFrame(function () {
          target.scrollIntoView({ block: 'start', behavior: 'auto' });
          // Move focus to the section's first interactive element if present
          const focusable = target.querySelector(
            'button, [href], input, select, textarea, [tabindex]:not([tabindex="-1"])'
          );
          if (focusable) {
            /** @type {HTMLElement} */ (focusable).focus();
          }
        });
      }

      // Consume the deep-link param from the URL
      if (new URLSearchParams(location.search).has('section')) {
        history.replaceState({}, '', location.pathname + location.hash);
      }
    } else {
      // Focus the close button as the first interactive element
      requestAnimationFrame(function () {
        const closeBtn = dialog && dialog.querySelector('.size-guide-modal__close');
        if (closeBtn) /** @type {HTMLElement} */ (closeBtn).focus();
      });
    }
  }

  /**
   * Close the size-guide modal.
   */
  function closeDialog() {
    if (!dialog) return;
    dialog.close();
  }

  /**
   * Check whether the modal is currently open.
   * @returns {boolean}
   */
  function isOpen() {
    return dialog ? dialog.open : false;
  }

  /**
   * Switch the displayed unit (cm | in) — client-side CSS toggle.
   * Applies data-bv-active-unit on the host element.
   * CSS rules [data-bv-active-unit="cm"] [data-bv-unit="in"] { display:none }
   * (and vice versa) hide the inactive unit's cells/labels.
   * @param {string} unit - 'cm' or 'in'
   */
  function setUnit(unit) {
    if (!host) return;
    host.setAttribute('data-bv-active-unit', unit);
    // Update aria-pressed states
    const btns = dialog && dialog.querySelectorAll('.size-guide-modal__unit-btn');
    if (btns) {
      btns.forEach(function (btn) {
        const isActive = btn.getAttribute('data-unit') === unit;
        btn.setAttribute('aria-pressed', isActive ? 'true' : 'false');
      });
    }
  }

  // ─── Analytics ───────────────────────────────────────────────────────────

  function emitCloseEvent() {
    // Consent gate: only emit if analytics consent is present
    const consent = window.benvel && window.benvel.consent;
    if (!consent || typeof consent.has !== 'function' || !consent.has('analytics')) {
      return;
    }

    const durationMs = openedAt ? Date.now() - openedAt : 0;

    document.dispatchEvent(
      new CustomEvent('benvel:size-guide-closed', {
        bubbles: true,
        detail: {
          section: (host && host.getAttribute('data-bv-active-unit')) || null,
          sourcePage: document.body.getAttribute('data-template') || 'unknown',
          durationMs: durationMs,
        },
      })
    );
  }

  // ─── Expose public API under window.benvel.sizeGuide ─────────────────────

  window.benvel = window.benvel || {};
  window.benvel.sizeGuide = {
    open: openSizeGuide,
    close: closeDialog,
    isOpen: isOpen,
    setUnit: setUnit,
  };
})();
